import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Grid,
  Box,
  Stack,
  Table,
  TableHead,
  TableRow,
  styled,
  TableCell,
  TableBody,
  Tooltip,
} from '@mui/material';
import { isEmpty, isEqual, omit } from 'lodash-es';
import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getSubmissionCount,
  ICandidate,
  IJob,
  ISubmissionSettingsResponse,
  uploadFileToS3,
} from 'wisestep-api';
import {
  ChatBoxHeader,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  WsDrawer,
  WsButton,
  WsColors,
  Label,
  WsFileUpload,
  Spinner,
} from 'wisestep-base';

import { parsedCandidatesType } from '../../../../features/candidates/utils/CandidateHelper';
import { MapAsSubmission } from '../../../../features/jobs/listing/components/mapAsSubmission';
import { failed, warning, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { submissionSchemaForBulk } from '../../schema/formSchema';
import IResume from '../../types/IResume';
import { trackCV, downloadCV, ITrackCvPages } from '../../utils/cvHelper';
import { saveBulkSubmission } from '../../utils/saveSubmission';
import JobSelection from '../JobSelection';
import WidgetJobManagement from '../WidgetJobManagement';

const CandidateBulkSubmissions: FC<{
  candidatesArray?: ICandidate[];
  job: IJob | null;
  close: () => void;
  openCandidateBulkSubmissions: boolean;
  submissionSetting: ISubmissionSettingsResponse;
  hideJobSelection?: boolean;
  parsedCandidatesData?: parsedCandidatesType[];
  fromParser?: boolean;
}> = ({
  openCandidateBulkSubmissions,
  candidatesArray = [],
  job,
  hideJobSelection,
  close,
  submissionSetting,
  parsedCandidatesData = [],
  fromParser,
}) => {
  const navigate = useNavigate();
  const [jobEntity, setJobEntity] = useState<IJob | null>(null);
  const [candidateIndex, setCandidateIndex] = useState(-1);
  const [processedCandidatesIndexes, setProcessedCandidatesIndexes] = useState<number[]>([]);
  const [candidateArray, setCandidateArray] = useState<ICandidate[]>(candidatesArray);
  const [disableSubmit, setDisableSubmit] = useState<boolean[]>(candidatesArray.map(() => false));
  const [showSubmissionForm, setShowSubmissionForm] = useState<boolean>(false);
  const [resume, setResume] = useState<{ index: number; resume: IResume }[]>([]);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [buttonMessage, setButtonMessage] = useState('');

  const onUpload = async (file: File[], field: string, index: number) => {
    const url = await uploadFileToS3(file[0]);
    if (field === 'resume') {
      const alteredCandidate = candidateArray[index];
      alteredCandidate.resumeLocation = url;
      alteredCandidate.resumeFileOriginalName = file[0].name;
      alteredCandidate.resumeUploadedOn = new Date();
      setResume([
        ...resume,
        ...[
          {
            index: index,
            resume: {
              resumeLocation: url,
              resumeFileOriginalName: file[0].name,
              resumeUploadedOn: new Date(),
            },
          },
        ],
      ]);

      const res = {
        candidateMappingId: '',
        email: '',
        gitHubLink: '',
        id: '',
        linkedInLink: '',
        phone: '',
        pinCode: '',
        resumeFileOriginalName: file[0].name,
        resumeLocation: url,
        resumeUploadedOn: new Date(),
      };
      alteredCandidate.mappedCandidateResumes = [res];
      candidateArray[index] = alteredCandidate;
      setCandidateArray([...candidateArray]);
    }
  };

  const resumeAction = (action: string, url: string | null, index: number) => {
    switch (action) {
      case 'view':
        !isEmpty(url) && trackCV('view', url, ITrackCvPages.SUBMISSION);
        break;
      case 'download':
        if (!isEmpty(url)) {
          trackCV('download', url, ITrackCvPages.SUBMISSION);
          url && downloadCV(url);
        }
        break;
      case 'removeCandidate':
        if (candidateArray[index]) {
          const alteredCandidate = candidateArray[index];
          alteredCandidate.resumeFileOriginalName = '';
          alteredCandidate.resumeLocation = '';
          alteredCandidate.resumeUploadedOn = null;
          candidateArray[index] = alteredCandidate;
          setCandidateArray([...candidateArray]);
        }
        break;
    }
  };

  const CustomTableHeadCell = styled(TableCell)(() => ({
    fontWeight: 600,
    fontSize: '14px',
    color: WsColors.TertiaryShades600,
    cursor: 'pointer',
    minWidth: '200px',
  }));

  useEffect(() => {
    setJobEntity(job);
  }, [job]);

  useEffect(() => {
    if (jobEntity && candidatesArray && !fromParser) {
      setCandidateArray((candidatesArrayNew: ICandidate[]) => {
        if (isEqual(candidatesArray, candidatesArrayNew)) {
          return candidatesArray;
        } else {
          return candidatesArrayNew;
        }
      });
    }
  }, [candidatesArray, fromParser, jobEntity]);

  const closeModal = () => close();

  const viewHandler = (index: number) => {
    setCandidateIndex(index), setShowSubmissionForm(true);
  };
  const OmittedSubmissionSetting = omit(submissionSetting, [
    'id',
    'workspaceId',
    'createdBy',
    'createdOn',
    'modifiedBy',
    'modifiedOn',
  ]);

  const fieldsForTable: { colName: string; label: string; mandatory: boolean }[] = Object.entries(
    OmittedSubmissionSetting
  )
    .flatMap(([key]) => {
      const arr: { colName: string; label: string; mandatory: boolean }[] = [];

      if (OmittedSubmissionSetting[key] && OmittedSubmissionSetting[key].hidden === false) {
        arr.push({
          colName: key,
          label: OmittedSubmissionSetting[key].label,
          mandatory: OmittedSubmissionSetting[key].required,
        });
      }
      return arr;
    })
    .concat({ colName: '', label: 'Actions', mandatory: false });

  const tableDataGenerator = (
    type: string,
    candidate: ICandidate,
    fileName: string,
    index: number
  ) => {
    let data: JSX.Element = <></>;
    const typeOfTableData = {
      ['name']: () => {
        data = <Label>{candidate?.name}</Label>;
      },
      ['email']: () => {
        data = <Label>{candidate?.email}</Label>;
      },
      ['uploadCV']: () => {
        data = fromParser ? (
          <Label>{fileName}</Label>
        ) : (
          <>
            {candidate?.resumeFileOriginalName ? (
              <Stack direction="row" spacing={2} mb={2}>
                <WsTypography variant={WsTypoVariants.BODY1}>
                  {candidate?.resumeFileOriginalName ?? ''}
                </WsTypography>
                <DownloadIcon
                  onClick={() =>
                    resumeAction && resumeAction('download', candidate.resumeLocation, index)
                  }
                  sx={{ color: WsColors.TheBlues500, fontSize: '24px', cursor: 'pointer' }}
                />
                <VisibilityIcon
                  onClick={() =>
                    resumeAction && resumeAction('view', candidate.resumeLocation, index)
                  }
                  sx={{ color: WsColors.TheBlues500, cursor: 'pointer' }}
                />
                <CloseIcon
                  onClick={() =>
                    resumeAction && resumeAction('removeCandidate', candidate.resumeLocation, index)
                  }
                  sx={{ color: WsColors.Red500, cursor: 'pointer' }}
                />
              </Stack>
            ) : (
              <WsFileUpload
                multiple={false}
                onUpload={(file: File[]) => onUpload && onUpload(file, 'resume', index)}
                name="resume"
                placeholderText=" "
                bgRequired={false}
                buttonText={'Upload File'}
                maxFileSize={3}
              />
            )}
          </>
        );
      },
      ['messengerId']: () => {
        data = (
          <Label>{(candidate?.messengers && candidate.messengers[0]?.messengerId) ?? ''}</Label>
        );
      },
      ['visaType']: () => {
        data = <Label>{candidate?.visaStatus && candidate?.visaStatus}</Label>;
      },
      ['visaValidity']: () => {
        data = <Label>{candidate?.visaValidity && candidate?.visaValidity}</Label>;
      },
      ['nationality']: () => {
        data = <Label>{candidate?.nationality && candidate?.nationality}</Label>;
      },
      ['alternateEmail']: () => {
        data = <Label>{candidate?.alternateEmails[0]}</Label>;
      },
      ['alternatePhoneNumber']: () => {
        data = <Label>{candidate?.alternatePhoneNumbers[0]}</Label>;
      },
      ['jobPreferences']: () => {
        data = (
          <Label>
            {candidate?.jobPreferenceContract &&
              `${candidate?.jobPreferenceContract ? 'Contract' : 'Permanent'}`}
          </Label>
        );
      },
      ['jobType']: () => {
        data = <Label>{candidate?.jobType && candidate?.jobType}</Label>;
      },
      ['phoneNumber']: () => {
        data = <Label>{candidate?.phoneNumber && candidate?.phoneNumber}</Label>;
      },
      ['linkedInURL']: () => {
        data = <Label>{(candidate?.linkedInURL && candidate?.linkedInURL) ?? ''}</Label>;
      },
      ['candidateID']: () => {
        data = <Label>{(candidate?.candidateId && candidate?.candidateId) ?? ''}</Label>;
      },
      ['currentLocation']: () => {
        data = (
          <Label>
            {candidate?.currentLocation
              ? `${candidate?.currentLocation?.city ?? ''}, ${
                  candidate?.currentLocation?.state ?? ''
                }, ${candidate?.currentLocation?.country ?? ''}`
              : ''}
          </Label>
        );
      },
      ['workAuthorization']: () => {
        data = <Label>{candidate.workAuthorization && candidate.workAuthorization}</Label>;
      },
      ['source']: () => {
        data = <Label>{candidate?.source && candidate?.source}</Label>;
      },
      ['skills']: () => {
        data = (
          <Label>
            {(candidate?.skills &&
              !isEmpty(candidate.skills) &&
              `${candidate?.skills[0] ?? ''},${candidate?.skills[1] ?? ''},${
                candidate?.skills[2] ?? ''
              }`) ??
              ''}
          </Label>
        );
      },
      ['totalExperience']: () => {
        data = (
          <Label>
            {(candidate?.totalYearsExperience && `${candidate?.totalYearsExperience} Years`) ?? ''}
          </Label>
        );
      },
      ['otherDocuments']: () => {
        data = <Label></Label>;
      },
      ['relevantExperience']: () => {
        data = (
          <Label>
            {(candidate?.relevantYearsExperience &&
              `${candidate?.relevantYearsExperience} Years`) ??
              ''}
          </Label>
        );
      },
      ['highestEducation']: () => {
        data = (
          <Label>
            {(candidate?.education &&
              !isEmpty(candidate.education) &&
              candidate.education[0].degree) ??
              ''}
          </Label>
        );
      },
      ['institutesAttended']: () => {
        data = (
          <Label>
            {(candidate?.educationQualifications &&
              !isEmpty(candidate.educationQualifications) &&
              candidate.educationQualifications[0]) ??
              ''}
          </Label>
        );
      },
      ['currentJobTitle']: () => {
        data = <Label>{(candidate?.currentJobTitle && candidate?.currentJobTitle) ?? ''}</Label>;
      },
      ['previousJobTitles']: () => {
        data = (
          <Label>
            {(candidate?.previousJobTitles &&
              !isEmpty(candidate.previousJobTitles) &&
              candidate.previousJobTitles[0]) ??
              ''}
          </Label>
        );
      },
      ['currentEmployer']: () => {
        data = <Label>{(candidate?.currentEmployer && candidate?.currentEmployer) ?? ''}</Label>;
      },
      ['previousEmployers']: () => {
        data = (
          <Label>
            {(candidate?.previousEmployers &&
              !isEmpty(candidate.previousEmployers) &&
              candidate.previousEmployers[0]) ??
              ''}
          </Label>
        );
      },
      ['certifications']: () => {
        data = (
          <Label>
            {(candidate?.certifications &&
              !isEmpty(candidate.certifications) &&
              candidate.certifications[0]) ??
              ''}
          </Label>
        );
      },
      ['preferredLocation']: () => {
        data = (
          <Label>
            {candidate?.preferredLocations &&
              !isEmpty(candidate.preferredLocations) &&
              (candidate?.preferredLocations[0]?.city ?? '',
              candidate?.preferredLocations[0]?.state ?? '',
              candidate?.preferredLocations[0]?.country ?? '')}
          </Label>
        );
      },
      ['relocationPreferences']: () => {
        data = (
          <Label>{(candidate.relocationPreference && candidate.relocationPreference) ?? ''}</Label>
        );
      },
      ['currentCompensation']: () => {
        data = (
          <Label>
            {(candidate?.currentSalary &&
              `${candidate?.currentSalaryCurrency} ${candidate?.currentSalary} `) ??
              ''}
          </Label>
        );
      },
      ['expectedCompensation']: () => {
        data = (
          <Label>
            {(candidate?.expectedBaseSalary &&
              `${candidate?.currentSalaryCurrency} ${candidate?.expectedBaseSalary} `) ??
              ''}
          </Label>
        );
      },
      ['communicationPreferences']: () => {
        data = (
          <Label>
            {candidate.commPreferenceEmail && candidate.commPreferenceEmail ? 'Email' : 'Phone'}
          </Label>
        );
      },
      ['noticePeriod']: () => {
        data = (
          <Label>
            {(candidate?.noticePeriodInDays && `${candidate?.noticePeriodInDays} Days`) ?? ''}
          </Label>
        );
      },
      ['passportNumber']: () => {
        data = <Label>{(candidate.passportNumber && candidate.passportNumber) ?? ''}</Label>;
      },
      ['age']: () => {
        data = <Label>{(candidate.age && candidate.age) ?? ''}</Label>;
      },
      ['passportType']: () => {
        data = <Label>{(candidate.passportType && candidate.passportType) ?? ''}</Label>;
      },
      ['reasonForChange']: () => {
        data = <Label>{(candidate.reasonForNewJob && candidate.reasonForNewJob) ?? ''}</Label>;
      },
      ['gender']: () => {
        data = <Label>{(candidate.gender && candidate.gender) ?? ''}</Label>;
      },
      ['interviewingAnywhereElse']: () => {
        data = (
          <Label>{candidate.interviewingWithOthers && candidate.interviewingWithOthers}</Label>
        );
      },
      ['yourAssessment']: () => {
        data = <Label>{(candidate.assessment && candidate.assessment) ?? ''}</Label>;
      },
      ['notes']: () => {
        data = <Label>{(candidate.customNote && candidate.customNote) ?? ''}</Label>;
      },
      ['questions']: () => {
        data = <Label>{(candidate.summary && candidate.summary) ?? ''}</Label>;
      },
      ['familyWillingToRelocate']: () => {
        data = (
          <Label>
            {(candidate.familyWillingToRelocate && candidate.familyWillingToRelocate) ?? ''}
          </Label>
        );
      },
      ['otherAllowance']: () => {
        data = (
          <Label>
            {(candidate?.otherCurrentAllowances &&
              `${candidate?.currentSalaryCurrency}${candidate?.otherCurrentAllowances} `) ??
              ''}
          </Label>
        );
      },
      ['otherInterviewStage']: () => {
        data = (
          <Label>{(candidate.otherInterviewStage && candidate.otherInterviewStage) ?? ''}</Label>
        );
      },
      ['workAuthorizationCategory']: () => {
        data = (
          <Label>
            {(candidate.workAuthorizationCategory && candidate.workAuthorizationCategory) ?? ''}
          </Label>
        );
      },
      ['currentJobIncludeTravel']: () => {
        data = (
          <Label>{(candidate.currentJobTravelType && candidate.currentJobTravelType) ?? ''}</Label>
        );
      },
      ['willingToTravel']: () => {
        data = <Label>{(candidate.willingToTravel && candidate.willingToTravel) ?? ''}</Label>;
      },
      ['willingToRelocate']: () => {
        data = <Label>{(candidate.willingToRelocate && candidate.willingToRelocate) ?? ''}</Label>;
      },
      ['expectedAllowance']: () => {
        data = (
          <Label>{candidate.otherExpectedAllowances && candidate.otherExpectedAllowances}</Label>
        );
      },
      ['candidatePersonalId']: () => {
        data = <Label>{candidate.candidatePersonalId && candidate.candidatePersonalId}</Label>;
      },
      ['dob']: () => {
        data = <Label>{candidate.dob && candidate.dob}</Label>;
      },
      ['postalAddress']: () => {
        data = <Label>{candidate.postalAddress && candidate.postalAddress}</Label>;
      },
      ['ssnNumber']: () => {
        data = <Label>{(candidate.ssnNumber && candidate.ssnNumber) ?? ''}</Label>;
      },
      ['']: () => {
        data = (
          <>
            <Stack direction={'row'} justifyContent={'space-around'}>
              <Tooltip title={'Edit'}>
                <WsButton
                  startIcon={<EditIcon />}
                  disabled={disableSubmit?.[index]}
                  onClick={() => viewHandler(index)}
                  variant="outlined"
                >
                  {processedCandidatesIndexes.indexOf(index) != -1 ? 'Edit' : 'Edit'}
                </WsButton>
              </Tooltip>

              <Tooltip title={'Submit'}>
                <WsButton
                  disabled={disableSubmit?.[index]}
                  onClick={() => {
                    setCandidateIndex(index);
                    cvValidation(index);
                  }}
                  variant="outlined"
                >
                  {processedCandidatesIndexes.indexOf(index) != -1 ? buttonMessage : 'Submit'}
                </WsButton>
              </Tooltip>
            </Stack>
          </>
        );
      },
    };
    if (Object.keys(typeOfTableData).includes(type)) typeOfTableData[type]();
    else console.log(`TypeName: ${type} Not Found`);
    return data;
  };
  const validateDataForSubmission = async (index: number) => {
    try {
      if (fromParser) {
        await submissionSchemaForBulk(submissionSetting).validate(
          parsedCandidatesData[index].candidate
        );
        return true;
      } else {
        await submissionSchemaForBulk(submissionSetting).validate(candidateArray[index]);
        return true;
      }
    } catch (error: any) {
      if (error.errors && Array.isArray(error.errors)) {
        const errorMessage = error.errors.join('\n');
        setNotification(warning(errorMessage));
        console.log(errorMessage);
      }
      console.log(error);
      return false;
    }
  };
  const cvValidation = (index: number) => {
    if (fromParser) {
      directSubmission(index);
    } else if (
      submissionSetting?.uploadCV.required &&
      isEmpty(candidateArray[index].resumeFileOriginalName)
    ) {
      setNotification(warning('Please upload CV'));
      disableSubmit[index] = false;
      setDisableSubmit([...disableSubmit]);
    } else {
      directSubmission(index);
    }
  };

  const directSubmission = async (index: number) => {
    disableSubmit[index] = true;
    setDisableSubmit([...disableSubmit]);

    if (await validateDataForSubmission(index)) {
      const candidateDetails = fromParser
        ? parsedCandidatesData[index].candidate
        : candidatesArray[index];
      const duplicateSubmission = await checkForDuplicateSubmission(candidateDetails);
      if (jobEntity && candidateDetails && !duplicateSubmission) {
        const response = await saveBulkSubmission(
          candidateDetails,
          jobEntity,
          resume[index] ? resume[index].resume : null
        );

        if (response) {
          setProcessedCandidatesIndexes([...processedCandidatesIndexes, ...[index]]);
          setButtonMessage('Submitted');
          setNotification(success('Candidate is submitted to selected job'));
        } else {
          disableSubmit[index] = false;
          setDisableSubmit([...disableSubmit]);
          setNotification(failed('Submission failed'));
        }
      } else {
        setProcessedCandidatesIndexes([...processedCandidatesIndexes, ...[index]]);
        setButtonMessage('Duplicate');
        setNotification(failed('Duplicate Submission, Candidate is already submitted for the job'));
      }
    } else {
      viewHandler(index);
      disableSubmit[index] = false;
      setDisableSubmit([...disableSubmit]);
    }
  };
  const checkForDuplicateSubmission = useCallback(
    async (data: ICandidate) => {
      if (data.email && jobEntity?.id) {
        const count = await getSubmissionCount({
          candidateEmail: data.email,
          jobId: jobEntity.id,
        });
        if (count.data.count <= 0) {
          return false;
        }
        return true;
      }
      return false;
    },
    [jobEntity?.id]
  );
  const prepareCandidate = () => {
    if (candidateIndex != -1) {
      const preparedCandidate = fromParser
        ? parsedCandidatesData[candidateIndex].candidate
        : candidatesArray[candidateIndex];
      preparedCandidate.jobAccountManagerEmail = null;
      preparedCandidate.jobClientRecruiterEmail = null;
      preparedCandidate.jobCode = null;
      preparedCandidate.jobWorkAssignment = null;
      preparedCandidate.jobType = null;
      preparedCandidate.workPermitDocuments = null;
      preparedCandidate.workPermitLocations = null;
      preparedCandidate.workPermits = null;
      preparedCandidate.workPreferences = null;
      preparedCandidate.workspaceId = null;
      preparedCandidate.workspaceName = null;

      if (fromParser) {
        preparedCandidate.resumeLocation = parsedCandidatesData[candidateIndex].fileUrl;
        preparedCandidate.resumeFileOriginalName = parsedCandidatesData[candidateIndex].fileName;
        preparedCandidate.resumeUploadedOn = new Date();
      }
      return preparedCandidate;
    }
  };
  return (
    <Box>
      <WsDrawer
        style={{ zIndex: '1300' }}
        size="full"
        hideHeader={true}
        open={openCandidateBulkSubmissions}
        onClose={() => closeModal()}
        hideOverflow={true}
      >
        {jobEntity ? (
          <Box>
            <ChatBoxHeader>
              <Grid container p={1}>
                <Grid item xs={3}>
                  <Box mt={0}>
                    <WsTypography
                      variant={WsTypoVariants.TITLE}
                      color={WsTypoColors.PRIMARY}
                      bold={true}
                    >
                      Candidate Bulk Submissions
                    </WsTypography>
                  </Box>
                </Grid>
                <Grid item xs={9}>
                  <Box sx={{ float: 'right', mr: 2 }}>
                    <Stack direction="row" spacing={3}>
                      {!hideJobSelection ? (
                        <WidgetJobManagement
                          selectedJob={jobEntity}
                          ss={12}
                          isFromMapAsLead={true}
                        />
                      ) : (
                        <WsTypography variant={WsTypoVariants.BODY1} bold={true}>
                          {`${jobEntity?.title} ( ${jobEntity?.jobCode} )`}
                        </WsTypography>
                      )}

                      <CloseIcon sx={{ cursor: 'pointer' }} onClick={() => closeModal()} />
                    </Stack>
                  </Box>
                </Grid>
              </Grid>
            </ChatBoxHeader>

            <Box sx={{ height: '100vh', overflow: 'scroll' }}>
              <Box sx={{ 'overflow-x': 'scroll', marginBottom: '150px' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {fieldsForTable.map((cellName, index) => (
                        <CustomTableHeadCell size="medium" key={index}>
                          {cellName.mandatory ? `${cellName.label} *` : `${cellName.label}`}
                        </CustomTableHeadCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {candidateArray.map((candidate, cIndex) => (
                      <Fragment key={cIndex}>
                        <TableRow>
                          {fieldsForTable.map((cellName, index) => (
                            <CustomTableHeadCell size="medium" key={index}>
                              {tableDataGenerator(cellName.colName, candidate, '', cIndex)}
                            </CustomTableHeadCell>
                          ))}
                        </TableRow>
                      </Fragment>
                    ))}
                    {parsedCandidatesData.map((candidate, cIndex) => (
                      <Fragment key={cIndex}>
                        <TableRow>
                          {candidate.isParsed ? (
                            <>
                              {fieldsForTable.map((cellName, index) => (
                                <CustomTableHeadCell size="medium" key={index}>
                                  {tableDataGenerator(
                                    cellName.colName,
                                    candidate.candidate,
                                    candidate.fileName,
                                    cIndex
                                  )}
                                </CustomTableHeadCell>
                              ))}
                            </>
                          ) : (
                            <>
                              <CustomTableHeadCell rowSpan={fieldsForTable.length}>
                                <Spinner />
                              </CustomTableHeadCell>
                            </>
                          )}
                        </TableRow>
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              <Grid
                mb={2}
                mr={2}
                sx={{ position: 'fixed', bottom: '0' }}
                container
                direction={'row-reverse'}
              >
                <Grid item>
                  <WsButton onClick={() => navigate('/submissions/')}>Complete</WsButton>
                </Grid>
              </Grid>
            </Box>

            <WsDrawer
              style={{ zIndex: '1300' }}
              size="medium"
              hideHeader={true}
              open={showSubmissionForm}
              onClose={() => setShowSubmissionForm(false)}
              hideOverflow={true}
            >
              <MapAsSubmission
                selectedJob={jobEntity}
                onClose={() => setShowSubmissionForm(false)}
                onSuccess={(msg) => {
                  setButtonMessage(msg);
                  disableSubmit[candidateIndex] = true;
                  setDisableSubmit([...disableSubmit]);
                  setProcessedCandidatesIndexes([
                    ...processedCandidatesIndexes,
                    ...[candidateIndex],
                  ]);
                }}
                selectedCandidate={prepareCandidate()}
                hideJobSelection={false}
                bulkSubmission={true}
              />
            </WsDrawer>
          </Box>
        ) : (
          <JobSelection getJob={(job) => setJobEntity(job)} job={job} onBackButton={closeModal} />
        )}
      </WsDrawer>
    </Box>
  );
};

export default CandidateBulkSubmissions;
